import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, ArrowLeft, Check, ShieldCheck, Phone, Mail, Send, Loader2, Play } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEO } from '../components/SEO';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GATEWAYS = {
  'home-solutions': {
    eyebrow: 'Home & Everyday Preservation',
    title: 'D-Freeze Home Solutions',
    lead: "Looking to preserve food at home? Our home scale freeze dryers live on our sister brand, HomeLyph.",
    description:
      "HomeLyph offers countertop and family-size freeze dryers designed for households, homesteaders and everyday food preservation, the same trusted engineering, sized for the home.",
    points: [
      'Compact, family-friendly freeze dryers',
      'Preserve harvests, meals and snacks for up to 25 years',
      'Simple, automated home operation',
      'Financing options available'
    ],
    heroImage: 'https://images.unsplash.com/photo-1757387240036-d7eb9b096c40?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
    infoImage: 'https://images.unsplash.com/photo-1598799170815-933217492614?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
    external: 'https://www.homelyph.com',
    externalLabel: 'homelyph.com',
    brand: 'HomeLyph',
    formIntro: 'Not sure which home unit is right for you? Send us a note and our team will point you in the right direction.',
    videoUrl: '',
    steps: [
      { title: 'Explore home units', desc: 'Browse countertop and family-size freeze dryers sized for household use.' },
      { title: 'Pick your size', desc: 'Choose a capacity that fits your kitchen, harvest and storage goals.' },
      { title: 'Preserve & store', desc: 'Freeze dry meals, snacks and produce and store them for up to 25 years.' }
    ],
    gallery: [
      { url: 'https://images.unsplash.com/photo-1598799170795-45f90ddfb662?crop=entropy&cs=srgb&fm=jpg&q=85&w=900', caption: 'Freeze-dried fruit, ready to store' },
      { url: 'https://images.unsplash.com/photo-1641291361624-38b69b86b1cf?crop=entropy&cs=srgb&fm=jpg&q=85&w=900', caption: 'Snacks & trail mixes that last' },
      { url: 'https://images.unsplash.com/photo-1598799170815-933217492614?crop=entropy&cs=srgb&fm=jpg&q=85&w=900', caption: 'Preserve full meals at home' },
      { url: 'https://images.unsplash.com/photo-1556216555-becc1cbd8b23?crop=entropy&cs=srgb&fm=jpg&q=85&w=900', caption: 'Batches sealed for up to 25 years' }
    ],
    faqs: [
      { q: 'Are home freeze dryers hard to operate?', a: 'No, HomeLyph units are fully automated. You load the trays, start a cycle, and the machine handles freezing, drying and finishing on its own.' },
      { q: 'How much can a home unit process?', a: 'Home units are sized for households and homesteaders, typically handling a few pounds to a family-batch per cycle.' },
      { q: 'Is financing available for home units?', a: 'Yes, HomeLyph offers financing options at checkout. Full details are on the HomeLyph site.' }
    ]
  },
  'repair-service': {
    eyebrow: 'Service & Rebuild',
    title: 'Freeze Dryer Rebuild',
    lead: 'Need a rebuild or replacement part? Our authorized service partner, LyphLine Freeze Dryers, has you covered.',
    description:
      'LyphLine Freeze Dryers handles diagnostics, rebuilds, parts and maintenance for freeze dryers so you can get back up and running quickly, with expert support.',
    points: [
      'Expert diagnostics and rebuilds',
      'Genuine replacement parts',
      'Maintenance and servicing',
      'Fast turnaround to minimize downtime'
    ],
    heroImage: 'https://images.unsplash.com/photo-1613206485381-b028e578e791?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
    infoImage: 'https://images.unsplash.com/photo-1606676539940-12768ce0e762?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
    external: 'https://www.lyphlinefreezedryers.com',
    externalLabel: 'lyphlinefreezedryers.com',
    brand: 'LyphLine Freeze Dryers',
    formIntro: 'Describe the issue or the part you need and our team will help get your freeze dryer serviced.',
    videoUrl: '',
    steps: [
      { title: 'Describe the issue', desc: 'Tell us the symptoms, model and any error codes so we can diagnose quickly.' },
      { title: 'Get a rebuild plan', desc: 'Our partner assesses the unit and provides a clear plan, parts list and timeline.' },
      { title: 'Back up and running', desc: 'Approve the plan and get your freeze dryer serviced with genuine parts and fast turnaround.' }
    ],
    gallery: [
      { url: 'https://images.unsplash.com/photo-1581093577421-f561a654a353?crop=entropy&cs=srgb&fm=jpg&q=85&w=900', caption: 'Full diagnostics on every unit' },
      { url: 'https://images.unsplash.com/photo-1518152006812-edab29b069ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=900', caption: 'Precision components serviced' },
      { url: 'https://images.unsplash.com/photo-1602052577122-f73b9710adba?crop=entropy&cs=srgb&fm=jpg&q=85&w=900', caption: 'Genuine replacement parts' },
      { url: 'https://images.unsplash.com/photo-1614308456595-a59d48697ea8?crop=entropy&cs=srgb&fm=jpg&q=85&w=900', caption: 'Back up and running fast' }
    ],
    faqs: [
      { q: 'Which machines can be serviced?', a: 'LyphLine technicians rebuild and modify select makes and models of freeze dryers. Send us your machine details and we will confirm whether yours is a fit.' },
      { q: 'How is turnaround time determined?', a: 'Turnaround is calculated after the unit is assessed and you approve the rebuild plan, so you always know the scope before work begins.' },
      { q: 'Do you use genuine replacement parts?', a: 'Yes, rebuilds use genuine replacement parts and, where possible, engineering enhancements to improve reliability.' }
    ]
  }
};

const LeaveNotice = ({ g }) => (
  <div className="bg-[#F8FAFC] border border-[#003456]/15 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
    <div className="flex items-start gap-3">
      <ShieldCheck className="h-5 w-5 text-[#003456] flex-shrink-0 mt-0.5" />
      <p className="text-sm text-gray-600">
        You're about to leave D-Freeze and continue to <span className="font-semibold text-[#003456]">{g.externalLabel}</span> ({g.brand}). It opens in a new tab.
      </p>
    </div>
    <a
      href={g.external}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-center gap-2 bg-[#003456] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#002B47] transition-colors whitespace-nowrap"
      data-testid="gateway-continue-btn"
    >
      Continue to {g.brand} <ExternalLink className="h-4 w-4" />
    </a>
  </div>
);

const ResourceGateway = ({ slug }) => {
  const g = GATEWAYS[slug] || GATEWAYS['home-solutions'];
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [videoUrl, setVideoUrl] = useState(g.videoUrl || '');

  useEffect(() => {
    axios.get(`${API_URL}/api/gateways/${slug}`)
      .then(res => setVideoUrl(res.data?.video_url || ''))
      .catch(() => {});
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in your name, email and message.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leads/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        form_source: slug
      });
      toast.success("Thanks! We've received your message and will be in touch shortly.");
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      console.error('Contact submit failed:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid={`gateway-${slug}`}>
      <SEO title={g.title} description={g.description} keywords={`${g.title}, ${g.brand}, freeze dryer`} />
      <Navbar />

      {/* Hero with photo */}
      <section className="relative pt-44 pb-20 section-padding overflow-hidden">
        <img src={g.heroImage} alt={g.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#003456] via-[#003456]/90 to-[#003456]/50"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <Link to="/products" className="inline-flex items-center gap-2 text-white/90 font-medium hover:text-white mb-6" data-testid="gateway-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to Products
          </Link>
          <span className="block text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em]">{g.eyebrow}</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mt-3 max-w-2xl">{g.title}</h1>
          <p className="text-lg text-white/85 mt-4 max-w-xl">{g.lead}</p>
          <a
            href={g.external}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-7 bg-[#CE8946] text-[#0a0203] font-semibold px-6 py-3 rounded-md hover:bg-[#00a9b1] transition-colors"
            data-testid="gateway-hero-cta"
          >
            Visit {g.brand} <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* Info + photo */}
      <section className="py-16 section-padding">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-[#0a0203] mb-4">What you'll find</h2>
            <p className="text-gray-700 mb-6">{g.description}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {g.points.map((p, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-[#CE8946] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="h-4 w-4 text-[#0a0203]" />
                  </span>
                  <span className="text-gray-700 text-sm">{p}</span>
                </div>
              ))}
            </div>
            <a
              href={g.external}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-8 bg-[#003456] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#002B47] transition-colors"
              data-testid="gateway-info-cta"
            >
              Visit {g.brand} <ExternalLink className="h-4 w-4" />
            </a>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-md">
            <img src={g.infoImage} alt={`${g.brand} highlight`} className="w-full h-full object-cover aspect-[4/3]" />
          </div>
        </div>
      </section>

      {/* Video */}
      <section className="py-16 section-padding bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <span className="block text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em]">Watch</span>
          <h2 className="text-3xl font-bold text-[#0a0203] mt-2 mb-8">{g.brand} overview</h2>
          <div className="relative rounded-3xl overflow-hidden shadow-lg aspect-video bg-[#003456]" data-testid="gateway-video">
            {videoUrl ? (
              <iframe src={videoUrl} title={`${g.brand} video`} className="absolute inset-0 w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-gradient-to-br from-[#003456] to-[#002B47]">
                <div className="w-20 h-20 rounded-full bg-[#CE8946] flex items-center justify-center mb-4 shadow-lg">
                  <Play className="h-8 w-8 text-[#0a0203] ml-1" fill="currentColor" />
                </div>
                <p className="font-semibold text-lg">Video coming soon</p>
                <p className="text-white/70 text-sm mt-1">Have a question? Reach out using the form below.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* How it works / What to expect */}
      <section className="py-16 section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <span className="block text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em]">What to expect</span>
            <h2 className="text-3xl font-bold text-[#0a0203] mt-2">How it works</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6" data-testid="gateway-steps">
            {g.steps.map((step, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm relative">
                <span className="w-10 h-10 rounded-full bg-[#003456] text-white font-bold flex items-center justify-center mb-4">{i + 1}</span>
                <h3 className="font-semibold text-[#0a0203] mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <span className="block text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em]">Gallery</span>
            <h2 className="text-3xl font-bold text-[#0a0203] mt-2">A closer look</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="gateway-gallery">
            {g.gallery.map((item, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-sm group">
                <img src={item.url} alt={item.caption || `${g.brand} ${i + 1}`} className="w-full h-full object-cover aspect-square group-hover:scale-105 transition-transform duration-500" />
                {item.caption && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-3 pt-8">
                    <p className="text-white text-xs sm:text-sm font-medium leading-snug" data-testid={`gateway-gallery-caption-${i}`}>{item.caption}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact form + leave notice */}
      <section className="py-16 section-padding bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl font-bold text-[#0a0203] mb-3">Have a question first?</h2>
            <p className="text-gray-600 mb-6">{g.formIntro}</p>
            <div className="space-y-3 mb-8">
              <a href="tel:1-888-223-1435" className="flex items-center gap-3 text-gray-700 hover:text-[#003456]">
                <span className="w-9 h-9 rounded-full bg-[#F8FAFC] flex items-center justify-center"><Phone className="h-4 w-4 text-[#003456]" /></span>
                1-888-223-1435
              </a>
              <a href="mailto:support@homelyph.com" className="flex items-center gap-3 text-gray-700 hover:text-[#003456]">
                <span className="w-9 h-9 rounded-full bg-[#F8FAFC] flex items-center justify-center"><Mail className="h-4 w-4 text-[#003456]" /></span>
                support@homelyph.com
              </a>
            </div>
            <LeaveNotice g={g} />
          </div>

          <form onSubmit={handleSubmit} className="bg-[#F8FAFC] rounded-3xl p-8" data-testid="gateway-contact-form">
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none"
                  data-testid="gateway-name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none"
                  data-testid="gateway-phone"
                />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none"
                data-testid="gateway-email"
                required
              />
            </div>
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
              <textarea
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full p-3 rounded-lg border border-gray-200 focus:border-[#003456] focus:outline-none resize-none"
                data-testid="gateway-message"
                required
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 bg-[#003456] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#002B47] transition-colors disabled:opacity-60"
              data-testid="gateway-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {submitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 section-padding">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8 text-center">
            <span className="block text-[#CE8946] font-semibold text-sm uppercase tracking-[0.2em]">FAQ</span>
            <h2 className="text-3xl font-bold text-[#0a0203] mt-2">Common questions</h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="gateway-faq">
            {g.faqs.map((f, i) => (
              <AccordionItem key={i} value={`gfaq-${i}`} className="border-b border-gray-100">
                <AccordionTrigger className="text-left text-[#0a0203] font-semibold hover:text-[#003456]" data-testid={`gateway-faq-q-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResourceGateway;
